/* Cart drawer behaviours: host-theme cart resync, remove confirmation, offers
   sheet, cancellation-policy accordion, and the preview-theme repaint fallback.

   These MUST load from the document rather than inline in the cart snippets. The
   drawer's contents are gated on `cart.empty?`, so on an empty cart the snippets
   — and any script inside them — are never rendered. Adding the first item
   re-renders the cart section and the theme inserts that HTML into the live
   document, and inserted <script> elements do not execute. The handlers would
   therefore stay unbound until a full page load happened to occur with a
   non-empty cart, leaving the offers sheet and the cancellation accordion dead.

   Every behaviour here is delegated on `document` and guarded by a window flag,
   so loading once per page covers every rendered instance (drawer, drawer-cards,
   cart page) and survives any number of cart re-renders. */

/* ---- Host-theme cart resync ---- */
(function () {
  if (window.__spectrumCartResync) return;
  window.__spectrumCartResync = true;

  /* Spectrum's cart actions (free gift, auto-add, discounts) mutate the cart AFTER
     Shopify has rendered the section HTML the theme morphs, so lines they add or
     remove are invisible to the cart UI. The row set, the item count and the line
     ORDER all stay a step behind — and because the stepper addresses lines by
     position, a stale row set means the next +/- edits the wrong line.

     The SDK announces the settled cart on its own event. The theme re-renders its
     cart section from the server when it sees `cart:update` carrying no `sections`
     payload, so translating one into the other is the whole fix. `detail.data` must
     exist: the theme's handler reads `detail.data.sections?.[id]` unguarded, and a
     bare CustomEvent has a null detail. */

  /* Correcting once is not enough, which is the whole reason this re-checks after
     every repaint rather than only when the SDK speaks.

     Two mutations can be in flight at once — a quantity edit, and the gift the
     actions pipeline adds or drops in response. Each carries its own section HTML,
     rendered at the moment its request was served, and the theme paints whichever
     response arrives last. An older response landing after a correction silently
     puts the stale markup back. Measured: a quantity edit made while a gift swap
     was in flight left the row reading 2 against a cart holding 1, and the shopper's
     next press acted on the number on screen — sending the line to zero and
     stranding the free gift at checkout.

     So staleness is established by comparison against a freshly fetched cart, and
     ANY repaint re-runs that comparison. The loop settles because a correct repaint
     leaves the rows matching the cart, which is a no-op. */

  var SETTLE_MS = 150;

  var syncing = false;
  var pending = false;
  var timer = null;

  function disagrees(cart) {
    if (!cart || !cart.items) return false;
    var rows = document.querySelectorAll('.spectrum-std-row[data-key]');
    if (rows.length !== cart.items.length) return true;
    var qtyByKey = {};
    for (var i = 0; i < cart.items.length; i++) qtyByKey[cart.items[i].key] = cart.items[i].quantity;
    for (var n = 0; n < rows.length; n++) {
      var key = rows[n].getAttribute('data-key');
      if (!(key in qtyByKey)) return true;
      /* Lines rendered without a stepper (free gift, bundle) carry no quantity to
         compare; their presence is the only thing being checked. */
      var input = rows[n].querySelector('.quantity-selector input');
      if (input && parseInt(input.value, 10) !== qtyByKey[key]) return true;
    }
    return false;
  }

  /* Clear the empty-cart classes once the cart stops being empty.

     `cart-drawer--empty` and `cart-drawer__content--empty` are stamped SERVER-SIDE by Liquid
     (`{% if cart.empty? %}` in header-actions.liquid) and nothing removed them again. Adding
     the first item repaints the drawer's CONTENTS over Ajax but leaves the dialog element
     itself untouched, so both classes survived — and
     `.cart-drawer--empty .cart-drawer__content { text-align: center }` then centred every
     descendant, while the empty-state block stayed visible so the drawer read as empty
     despite holding a line.

     Only ever visible on the FIRST add, because that is the only one starting from an empty
     cart; a refresh re-ran the Liquid and looked correct, which is what made it seem
     intermittent.

     Toggled, not removed: emptying the cart over Ajax has the same bug in reverse (the drawer
     keeps its filled styling), just less noticeable. Driven by item_count from the /cart.js
     response already fetched below rather than by counting rows, because at `cart:update`
     time the repaint may not have landed yet.

     ONLY the dialog class. `cart-drawer__content--empty` is hardcoded inside the
     `empty_cart_drawer_content` capture, which Liquid injects into the dialog only when the
     cart is empty — the repaint swaps that whole block out, so the class leaves with it and
     there is nothing to clear. Touching it here would mean reaching into markup Liquid owns
     and, in the emptying direction, stamping it onto a block about to be replaced. */
  function syncEmptyState(itemCount) {
    if (typeof itemCount !== 'number') return;
    var dlg = document.querySelector('.cart-drawer__dialog');
    if (dlg) dlg.classList.toggle('cart-drawer--empty', itemCount === 0);
  }

  /* The authoritative sync above only lands after the settle delay AND the /cart.js
     round-trip — a second or more. The drawer has already opened and painted by then, so the
     stale centring is visible and then snaps left, which is worse than the original bug: it
     is now a flash rather than a steady state.

     So clear it from the DOM the moment the rows exist, without waiting for the network. A
     rendered line row means the cart is not empty; that is enough to drop the class. This
     never SETS the class — going empty is the slow direction and nobody notices a drawer
     styling itself as empty a beat late, whereas everyone notices text jumping.

     Re-checked over the next few frames because `cart:update` can fire before the repaint
     lands, so the rows may not be in the DOM on the first look. */
  function clearEmptyIfRowsPresent() {
    var dlg = document.querySelector('.cart-drawer__dialog');
    if (!dlg || !dlg.classList.contains('cart-drawer--empty')) return true;
    if (dlg.querySelector('[data-key], .cart-items__table-row')) {
      dlg.classList.remove('cart-drawer--empty');
      return true;
    }
    return false;
  }
  function clearEmptySoon() {
    if (clearEmptyIfRowsPresent()) return;
    var frames = 0;
    (function again() {
      if (clearEmptyIfRowsPresent() || ++frames > 20) return;
      requestAnimationFrame(again);
    })();
  }
  document.addEventListener('cart:update', clearEmptySoon);
  document.addEventListener('$spectrum:cart:updated', clearEmptySoon);

  function check() {
    if (syncing) {
      /* A repaint landed mid-check; whatever we are about to conclude may already
         be out of date, so run again once this pass finishes. */
      pending = true;
      return;
    }
    /* Keyed off the component, not off the rows: an empty cart renders zero rows but
       still mounts the component, and "UI showing empty while the cart has lines" is
       precisely a state that needs repainting. Absent means no cart on this page. */
    if (!document.querySelector('cart-items-component')) return;

    syncing = true;
    fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then(function (res) {
        return res.ok ? res.json() : null;
      })
      .then(function (cart) {
        if (!cart) return;
        syncEmptyState(cart.item_count);
        /* Settles on its own: a correct repaint leaves the rows matching the cart,
           and the next pass finds nothing to do. */
        if (!disagrees(cart)) return;
        /* The theme re-renders its cart section from the server when it sees
           `cart:update` carrying no `sections` payload. `detail.data` must exist:
           its handler reads `detail.data.sections?.[id]` unguarded, and a bare
           CustomEvent has a null detail. */
        document.dispatchEvent(
          new CustomEvent('cart:update', {
            bubbles: true,
            detail: { resource: cart, sourceId: 'spectrum-cart-actions', data: {} },
          })
        );
      })
      .catch(function () {
        /* A failed check leaves the markup alone; the next repaint or mutation
           retries, and a page load renders from the cart itself. */
      })
      .then(function () {
        syncing = false;
        if (pending) {
          pending = false;
          schedule();
        }
      });
  }

  /* Collapses the burst a single render produces, and the cascade of a removal
     dropping the gift it qualified for. */
  function schedule() {
    clearTimeout(timer);
    timer = setTimeout(check, SETTLE_MS);
  }

  /* Two triggers, both load-bearing. `$spectrum:cart:updated` is the earliest signal
     that the actions pipeline settled. `cart:update` fires on every repaint the theme
     performs — including the late one carrying superseded markup, which no cart event
     announces, and which listening to the SDK alone therefore misses. */
  document.addEventListener('$spectrum:cart:updated', schedule);
  document.addEventListener('cart:update', schedule);

})();

/* ---- Remove confirmation ---- */
(function () {
  if (window.__spectrumRemoveDialog) return;
  window.__spectrumRemoveDialog = true;

  var pending = null;

  function build() {
    var d = document.getElementById('spectrum-remove-dialog');
    if (d) return d;
    d = document.createElement('dialog');
    d.id = 'spectrum-remove-dialog';
    d.className = 'spectrum-remove-dialog';
    d.innerHTML =
      '<div class="spectrum-remove-dialog__head">' +
        '<p class="spectrum-remove-dialog__title">Remove item from cart ?</p>' +
        '<button type="button" class="spectrum-remove-dialog__x" aria-label="Close">' +
          '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M18 6 6 18M6 6l12 12"/></svg>' +
        '</button>' +
      '</div>' +
      '<p class="spectrum-remove-dialog__body">Sure you want to Remove, this action can’t be undone. You’ll need to add it again</p>' +
      '<div class="spectrum-remove-dialog__actions">' +
        '<button type="button" class="spectrum-remove-dialog__cancel">Cancel</button>' +
        '<button type="button" class="spectrum-remove-dialog__confirm">Remove</button>' +
      '</div>';
    document.body.appendChild(d);
    d.addEventListener('click', function (e) {
      if (e.target === d) closeDialog();
    });
    return d;
  }

  /* A dialog opened from `pointerdown` reaches the top layer BEFORE the gesture's
     compatibility `click` is dispatched. Touch browsers hit-test that click at the
     touch point a second time, and a modal dialog's backdrop hit-tests to the dialog
     element itself — so the click lands on the dialog and trips its click-outside
     handler, opening and shutting the confirm within a single tap. (Mouse is immune:
     the click target is resolved to the mousedown/mouseup common ancestor instead.)
     Swallow the one click that closes out the gesture that opened the dialog. */
  var eatClick = null;
  function swallowNextClick() {
    if (eatClick) return;
    var timer;
    var stop = function () {
      document.removeEventListener('click', eatClick, true);
      clearTimeout(timer);
      eatClick = null;
    };
    eatClick = function (e) {
      e.preventDefault();
      e.stopImmediatePropagation();
      stop();
    };
    document.addEventListener('click', eatClick, true);
    /* Disarm if no click follows — a cancelled gesture must not eat a later, real one. */
    timer = setTimeout(stop, 700);
  }

  /* Bumped by every open and close. A close defers its actual `.close()` until the
     exit transition ends, so it has to check that it is still the newest intent —
     otherwise a re-open during that window gets shut by the older close's timer. */
  var closeGen = 0;

  /* Pin the confirm inside the open cart drawer's rect. Setting all four insets plus
     `margin:auto` centres the dialog within that band and leaves its own max-width intact.
     Clearing the inline styles restores the UA's viewport centring. */
  function anchorToDrawer(d) {
    var host = document.querySelector('.cart-drawer__dialog[open]');
    var r = host && host.getBoundingClientRect();
    if (!r || !r.width || !r.height) {
      d.style.removeProperty('left'); d.style.removeProperty('right');
      d.style.removeProperty('top'); d.style.removeProperty('bottom');
      d.style.removeProperty('margin');
      return;
    }
    d.style.left = r.left + 'px';
    d.style.right = Math.max(0, window.innerWidth - r.right) + 'px';
    d.style.top = r.top + 'px';
    d.style.bottom = Math.max(0, window.innerHeight - r.bottom) + 'px';
    d.style.margin = 'auto';
  }
  /* Re-anchor while open: an orientation change or a resize moves the drawer under it. */
  window.addEventListener('resize', function () {
    var d = document.getElementById('spectrum-remove-dialog');
    if (d && d.open) anchorToDrawer(d);
  });

  function openDialog(removeBtn) {
    var d = build();
    closeGen++;
    pending = removeBtn;
    /* Centre the confirm over the CART DRAWER, not the viewport. `showModal()` puts the
       dialog in the browser's top layer, whose default is to centre on the whole screen —
       which reads as a page-level modal for what is really a decision about one line in the
       panel you are looking at. Anchor it to the drawer's band instead.

       Done by measurement rather than CSS because the drawer is itself a top-layer <dialog>:
       the confirm is not its descendant, so it cannot inherit the drawer's box. Falls back to
       viewport centring when no drawer is open — the /cart page has no drawer to anchor to,
       and on mobile the drawer is full-width so the two are the same thing anyway.

       The backdrop still dims the whole screen; only the panel moves. */
    if (typeof d.showModal === 'function') { if (!d.open) d.showModal(); }
    else d.setAttribute('open', '');
    anchorToDrawer(d);
    /* Toggle .is-open on the next frame so the CSS transition runs from the
       start state (mirrors spectrum-quick-add.js for the mini-PDP dialog). */
    requestAnimationFrame(function () {
      requestAnimationFrame(function () { d.classList.add('is-open'); });
    });
  }
  function closeDialog() {
    var d = document.getElementById('spectrum-remove-dialog');
    var gen = ++closeGen;
    pending = null;
    if (!d) return;
    d.classList.remove('is-open');
    var finish = function () { if (typeof d.close === 'function' && d.open) d.close(); else d.removeAttribute('open'); };
    if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) { finish(); return; }
    /* Let the exit transition play, then actually close. transitionend (first of
       transform/opacity to finish) drives it, with a timeout fallback. */
    var done = false;
    var end = function (e) {
      if (e && e.target !== d) return;
      if (done) return;
      done = true;
      d.removeEventListener('transitionend', end);
      if (gen !== closeGen) return; /* re-opened meanwhile — leave it open */
      finish();
    };
    d.addEventListener('transitionend', end);
    setTimeout(end, 400); /* fallback > longest transition (mobile 0.34s) */
  }

  /* Pressing '-' at the minimum quantity removes the line on the live cart (with a confirm),
     rather than no-op flooring. Intercept in the capture phase so the native stepper's
     decrement never runs, then route confirmation to the line's existing remove control.
     Bound on pointerdown (fires first, before any section re-render can swap the button out)
     and on click as a fallback; openDialog is guarded so the pair can't double-open. */
  /* One press decides once. `pointerdown` and `click` are two events of the SAME
     press, and the native stepper decrements between them — so re-reading the input
     on the click sees a quantity the first half of that very press produced. At two,
     pointerdown correctly declined to intercept, the stepper wrote 1, and the click
     then read "already at minimum" and opened the confirm: one press both decremented
     AND offered to remove the line, and confirming stranded the free gift.

     The pairing is established by the events themselves, not by a clock. An earlier
     attempt expired the pairing after 700ms and held only while the compat click
     arrived inside that window — measured good to 650ms and broken by 900ms, which a
     slow phone reaches easily on a page this heavy.

     A pointer press always opens with pointerdown, so a leftover token can only
     belong to a press that produced no click (dragged off the button), and the next
     press re-arms it before its own click arrives — a stale token cannot mute a real
     press. Keyboard activation dispatches a click with `detail === 0` and no
     pointerdown, so it is never treated as somebody else's second half. */
  var pointerRuled = false;

  function interceptMinus(e) {
    var minus = e.target.closest ? e.target.closest('.spectrum-std .quantity-minus') : null;
    if (!minus) return;
    /* `detail > 0` marks a click produced by a pointer gesture; only those can be the
       trailing half of a pointerdown this handler already ruled on. */
    if (e.type === 'click' && pointerRuled && e.detail > 0) {
      pointerRuled = false;
      return;
    }
    var row = minus.closest('.spectrum-std');
    if (!row) return;
    var input = row.querySelector('.quantity-selector input');
    var qty = input ? parseInt(input.value, 10) : NaN;
    var min = input ? parseInt(input.getAttribute('min') || '1', 10) : 1;
    /* Ruled either way — intercepted, or deliberately allowed through to the stepper.
       Both outcomes must stop this press's click from reconsidering. */
    if (e.type === 'pointerdown') pointerRuled = true;
    if (!isNaN(qty) && qty <= min) {
      e.preventDefault();
      e.stopImmediatePropagation();
      /* Only the pointerdown path produces a trailing compat click to swallow. */
      if (e.type === 'pointerdown') swallowNextClick();
      openDialog(row.querySelector('.spectrum-std__remove'));
    }
  }
  document.addEventListener('pointerdown', interceptMinus, true);
  document.addEventListener('click', interceptMinus, true);

  /* The visible trash icon opens the same confirm dialog; on confirm it clicks the
     line's hidden native remove control, which is kept in the DOM for this purpose. */
  document.addEventListener('click', function (e) {
    var trash = e.target.closest ? e.target.closest('.spectrum-std__trash') : null;
    if (!trash) return;
    e.preventDefault();
    var row = trash.closest('.spectrum-std');
    if (row) openDialog(row.querySelector('.spectrum-std__remove'));
  });

  document.addEventListener('click', function (e) {
    if (!e.target.closest) return;
    if (e.target.closest('.spectrum-remove-dialog__confirm')) {
      var btn = pending;
      closeDialog();
      if (btn) btn.click();
    } else if (e.target.closest('.spectrum-remove-dialog__cancel') || e.target.closest('.spectrum-remove-dialog__x')) {
      closeDialog();
    }
  });
})();

/* ---- Offers sheet ---- */
(function () {
  if (window.__spectrumOffersSheet) return;
  window.__spectrumOffersSheet = true;
  // Scroll lock. The sheet almost always opens from inside the already-open cart drawer,
  // which the theme's DialogComponent has locked via body{position:fixed}; a <details
  // scroll-lock> or other dialog instead sets html[scroll-lock]. Either means the page is
  // already held, and a second lock here would toggle html overflow on the exact frame the
  // sheet slides in — a visible jerk. So only lock when nothing else has, and unlock only
  // what we locked (opening on the cart page, where no drawer holds the background).
  var sheetLockedScroll = false;
  function bgScrollLocked() {
    return document.body.style.position === 'fixed' || document.documentElement.hasAttribute('scroll-lock');
  }
  function lockScroll() {
    if (bgScrollLocked()) return;
    document.documentElement.setAttribute('scroll-lock', '');
    sheetLockedScroll = true;
  }
  function unlockScroll() {
    if (!sheetLockedScroll) return;
    document.documentElement.removeAttribute('scroll-lock');
    sheetLockedScroll = false;
  }
  // Backdrop click on an offers/details dialog closes ONLY that dialog.
  // These sheets open from inside the cart drawer, whose DialogComponent
  // light-dismiss uses a COORDINATE check when the click target is a
  // <dialog> (which a backdrop click is) — so without this, the same click
  // also reads as "outside the cart" and closes the cart. Capture-phase +
  // stopPropagation keeps the click from ever reaching the cart handler.
  document.addEventListener('click', function (e) {
    var t = e.target;
    if (t instanceof HTMLDialogElement && t.open &&
        (t.classList.contains('spectrum-offers-sheet') || t.classList.contains('spectrum-offer-details'))) {
      e.stopPropagation();
      t.close();
    }
  }, true);
  // The offers section is rendered INSIDE the cart-drawer <dialog>. After a
  // cart re-render (add-to-cart / qty change) that drawer dialog can lose its
  // open state, and a modal opened while nested in a display:none <dialog>
  // subtree lays out at 0×0 — it opens but paints invisible. That is the
  // "View all sometimes does nothing after adding to cart" bug. Relocating
  // the dialog to <body> before showModal() lifts it out of the drawer
  // subtree so it always renders. (Verified live: nested → 0px wide, body → 450px.)
  // A later cart morph re-creates a fresh sheet inside the section, so we also
  // drop any stale relocated copy left in <body> from a previous open.
  // Animated close. The sheets transition out via `overlay ... allow-discrete`, which is
  // Chrome-only — Safari/iOS drop the dialog from the top layer the instant close() runs, so
  // the slide-out never renders. Flagging [data-closing] keeps the dialog open while the
  // transform plays, then closes it for real. The timeout covers reduced-motion / no
  // transitionend (where the transition never fires at all).
  function closeDialogAnimated(dlg) {
    if (!dlg || !dlg.open || dlg.hasAttribute('data-closing')) return;
    var done = false;
    function finish() {
      if (done) return;
      done = true;
      dlg.removeEventListener('transitionend', onEnd);
      dlg.removeAttribute('data-closing');
      try { dlg.close(); } catch (e) {}
    }
    function onEnd(e) {
      if (e.target === dlg && e.propertyName === 'transform') finish();
    }
    dlg.addEventListener('transitionend', onEnd);
    dlg.setAttribute('data-closing', '');
    // With transitions off (reduced motion) transitionend never fires, and waiting on the
    // fallback would leave an invisible dialog holding the top layer. Close at once instead.
    if (!parseFloat(getComputedStyle(dlg).transitionDuration)) { finish(); return; }
    setTimeout(finish, 400);
  }

  /* ---------------------------------------------------------------------------
   * Offer terms, fetched on demand.
   *
   * The T&C panels used to be server-rendered one per offer — 24 dialogs, ~96KB, 42% of
   * the cart section — re-rendered and re-morphed on every quantity change even though a
   * shopper opens at most one. They now live behind `/cart?view=spectrum-offers-data` and
   * are rendered into a single shell on first tap.
   * ------------------------------------------------------------------------- */
  var offersData = null;      // cached offers array once fetched
  var offersPromise = null;   // in-flight fetch, so rapid taps share one request

  function escHtml(v) {
    return String(v == null ? '' : v)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;').replace(/'/g, '&#039;');
  }

  function loadOffersData() {
    if (offersData) return Promise.resolve(offersData);
    if (offersPromise) return offersPromise;
    var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    // Deliberately NO `Accept: application/json` header — Shopify honours it over the
    // `view` param and would serve its own cart JSON instead of the template.
    offersPromise = fetch(root + 'cart?view=spectrum-offers-data')
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (d) {
        offersData = (d && d.offers) || null;
        offersPromise = null;
        return offersData;
      })
      .catch(function () { offersPromise = null; return null; });
    return offersPromise;
  }

  function offerBodyHtml(offer) {
    var logo = offer.asset
      ? '<img src="' + escHtml(offer.asset) + '" alt="" width="30" height="30" loading="lazy">'
      : '<span class="spectrum-offer-details__logo-text">' + escHtml(offer.logo_text) + '</span>';
    var html =
      '<div class="spectrum-offer-details__offer">' +
        '<span class="spectrum-offer-details__logo" style="background: ' + escHtml(offer.logo_color || '#f7f7f7') + ';">' + logo + '</span>' +
        '<span class="spectrum-offer-details__divider"></span>' +
        '<span class="spectrum-offer-details__offer-body">' +
          '<span class="spectrum-offer-details__offer-title">' + escHtml(offer.title) + '</span>' +
          '<span class="spectrum-offer-details__offer-method">' + escHtml(offer.method) + '</span>' +
        '</span>' +
      '</div>';
    var terms = offer.terms || [];
    if (terms.length) {
      // Lead term pinned, the rest scrolling — the offer card, headline and footer CTA stay
      // on screen however long the list runs (CMS terms reach 22 on the No Cost EMI offers).
      html +=
        '<div class="spectrum-offer-details__terms-card">' +
          '<ul class="spectrum-offer-details__terms spectrum-offer-details__terms--lead">' +
            '<li class="spectrum-offer-details__term spectrum-offer-details__term--lead">' + escHtml(terms[0]) + '</li>' +
          '</ul>';
      if (terms.length > 1) {
        html += '<ul class="spectrum-offer-details__terms spectrum-offer-details__terms--rest">';
        for (var i = 1; i < terms.length; i++) {
          html += '<li class="spectrum-offer-details__term">' + escHtml(terms[i]) + '</li>';
        }
        html += '</ul>';
      }
      html += '</div>';
    }
    return html;
  }

  /**
   * Fill the shared shell for `idx`. Renders synchronously from cache when we have it, so
   * a second open never flashes. `opener` is the sheet ticket that was tapped: its own
   * copy of the offer text is the fail-open fallback, so a failed fetch still shows the
   * shopper something rather than an empty dialog.
   */
  function fillOfferShell(panel, idx, opener) {
    var body = panel.querySelector('[data-spectrum-offer-shell-body]');
    if (!body) return;
    var n = parseInt(idx, 10);

    function render(offers) {
      var offer = offers && offers.filter(function (o) { return o.index === n; })[0];
      if (offer) { body.innerHTML = offerBodyHtml(offer); return; }
      // `opener` IS the ticket (<li class="spectrum-offer-ticket" data-spectrum-offer-open>),
      // so its own text is the fail-open fallback: a failed fetch still shows the shopper
      // the offer rather than an empty dialog.
      var fallbackText = (opener && opener.textContent.trim()) || 'Offer applied at checkout.';
      body.innerHTML =
        '<div class="spectrum-offer-details__terms-card">' +
          '<ul class="spectrum-offer-details__terms spectrum-offer-details__terms--lead">' +
            '<li class="spectrum-offer-details__term spectrum-offer-details__term--lead">' +
              escHtml(fallbackText) +
            '</li>' +
          '</ul>' +
        '</div>';
    }

    if (offersData) { render(offersData); return; }
    // Shimmer, not a "Loading…" line: the text placeholder resized the dialog when the
    // real terms landed, which read as a jitter. These bars approximate the finished
    // layout, so the swap is a content change rather than a resize. Only ever seen on the
    // first open — afterwards `offersData` is cached and render() is synchronous.
    body.innerHTML =
      '<div class="spectrum-offer-details__offer">' +
        '<span class="spectrum-offer-details__skel-logo"></span>' +
        '<span class="spectrum-offer-details__divider"></span>' +
        '<span class="spectrum-offer-details__offer-body">' +
          '<span class="spectrum-offer-details__skel-line spectrum-offer-details__skel-line--lead"></span>' +
        '</span>' +
      '</div>' +
      '<div class="spectrum-offer-details__terms-card">' +
        '<span class="spectrum-offer-details__skel-line spectrum-offer-details__skel-line--lead"></span>' +
        '<span class="spectrum-offer-details__skel-line"></span>' +
        '<span class="spectrum-offer-details__skel-line spectrum-offer-details__skel-line--short"></span>' +
        '<span class="spectrum-offer-details__skel-line"></span>' +
        '<span class="spectrum-offer-details__skel-line spectrum-offer-details__skel-line--short"></span>' +
      '</div>';
    loadOffersData().then(render);
  }

  function openDialogAtBody(dlg, staleSelector) {
    if (!dlg || typeof dlg.showModal !== 'function') return false;
    // A reopen must not inherit a half-finished close.
    dlg.removeAttribute('data-closing');
    // ESC fires `cancel` then closes instantly; take it over so it animates too.
    if (!dlg.spectrumCancelWired) {
      dlg.spectrumCancelWired = true;
      dlg.addEventListener('cancel', function (e) {
        e.preventDefault();
        closeDialogAnimated(dlg);
      });
    }
    document.querySelectorAll('body > ' + staleSelector).forEach(function (old) {
      if (old !== dlg) { try { old.close(); } catch (e) {} old.remove(); }
    });
    // Remember the owning section BEFORE the move — once the dialog is a body
    // child, closest('.shopify-section') inside it returns null, which would
    // lose the section id the sheet needs to resolve its owning section.
    var owner = dlg.closest('.shopify-section');
    if (owner) dlg.setAttribute('data-spectrum-section-id', owner.id.replace('shopify-section-', ''));
    if (dlg.parentNode !== document.body) document.body.appendChild(dlg);
    try { if (dlg.open) dlg.close(); dlg.showModal(); } catch (e) { return false; }
    /* showModal() autofocuses the first focusable child — here the back / close
       control — and the browser paints the theme's :focus-visible ring on it, so the
       sheet opens with a box drawn around that icon until the next tap lands
       elsewhere. Park focus on the dialog instead: it keeps focus inside the modal
       (so ESC and the tab cycle still work, and screen readers announce the sheet)
       while a programmatically focused container draws no ring. */
    dlg.setAttribute('tabindex', '-1');
    try { dlg.focus({ preventScroll: true }); } catch (e) { dlg.focus(); }
    return true;
  }
  // Section id for a node that may live inside a relocated (body-level) dialog.
  function sectionIdFor(node) {
    var el = node.closest('.shopify-section');
    if (el) return el.id.replace('shopify-section-', '');
    var host = node.closest('[data-spectrum-section-id]');
    return host ? host.getAttribute('data-spectrum-section-id') : null;
  }
  document.addEventListener('click', function (e) {
    var opener = e.target.closest('[data-spectrum-offers-open]');
    if (opener) {
      var sec = opener.closest('.spectrum-cart-offers');
      var sheet = (sec && sec.querySelector('dialog.spectrum-offers-sheet')) ||
                  document.querySelector('dialog.spectrum-offers-sheet');
      if (openDialogAtBody(sheet, 'dialog.spectrum-offers-sheet')) {
        lockScroll();
        sheet.addEventListener('close', unlockScroll, { once: true });
      }
      return;
    }
    // Tap an offer ticket → open its "Terms and conditions" panel by index. It opens over the
    // already-open offers sheet (also relocated to <body>), so no extra lock here; closing
    // returns to the sheet underneath. Same 0×0 nesting fix applies to these panels.
    var detailOpener = e.target.closest('[data-spectrum-offer-open]');
    if (detailOpener) {
      var idx = detailOpener.getAttribute('data-spectrum-offer-open');
      // ONE shell for every offer. There used to be a panel per offer, which cost ~96KB of
      // the cart section (42%) on every re-render, and made the stale-copy cleanup inside
      // openDialogAtBody dangerous: opening offer B could delete offer A's relocated copy
      // and leave A permanently unclickable. With a single shell there are no duplicates
      // to confuse, and the terms arrive from `/cart?view=spectrum-offers-data`.
      var sel = 'dialog.spectrum-offer-details[data-spectrum-offer-shell]';
      var panel = document.querySelector('.spectrum-cart-offers ' + sel) ||
                  document.querySelector(sel);
      if (!panel) return;
      fillOfferShell(panel, idx, detailOpener);
      openDialogAtBody(panel, sel);
      return;
    }
    if (e.target.closest('[data-spectrum-offer-close]')) {
      var openDetails = e.target.closest('dialog.spectrum-offer-details');
      if (openDetails) closeDialogAnimated(openDetails);
      return;
    }
    if (e.target.classList && e.target.classList.contains('spectrum-offer-details')) {
      closeDialogAnimated(e.target);
      return;
    }
    if (e.target.closest('[data-spectrum-offers-close]')) {
      var open = e.target.closest('dialog.spectrum-offers-sheet');
      if (open) closeDialogAnimated(open);
      return;
    }
    if (e.target.classList && e.target.classList.contains('spectrum-offers-sheet')) {
      closeDialogAnimated(e.target);
    }
  });
  // Keyboard: the offer tickets are role="button"; fire their click on Enter/Space.
  document.addEventListener('keydown', function (e) {
    if (e.key !== 'Enter' && e.key !== ' ') return;
    var ticket = e.target.closest && e.target.closest('[data-spectrum-offer-open]');
    if (ticket) { e.preventDefault(); ticket.click(); }
  });

})();

/* ---- Cancellation-policy accordion ---- */
// Cancellation-policy accordion toggle. Delegated on document + guarded so the
// one listener covers every rendered instance (drawer, drawer-cards, page) and
// survives cart re-renders. The grid-rows CSS animates open/close.
(function () {
  if (window.__spectrumCancelInit) return;
  window.__spectrumCancelInit = true;
  document.addEventListener('click', function (e) {
    var btn = e.target.closest && e.target.closest('[data-spectrum-cancel-toggle]');
    if (!btn) return;
    var card = btn.closest('[data-spectrum-cancel]');
    if (!card) return;
    var open = card.classList.toggle('is-open');
    btn.setAttribute('aria-expanded', open ? 'true' : 'false');
  });
})();

/* ---- Preview-theme repaint fallback ---- */
(function () {
  /* Preview-only cart repaint.

     On a preview/development theme, Shopify's Section Rendering API does not serve this theme's
     sections — a cart change (/cart/change) returns the updated cart data but an EMPTY `sections`
     object, so Horizon's morph has no HTML to apply and the cart's prices stay stale. The
     published theme does not have this problem (its sections render), so this fallback is gated
     to non-live themes and is inert in production.

     When the cart changes, refetch the cart through a URL that forces the preview theme to render
     (preview_theme_id + the edge debug destination), then repaint the display-only price elements
     from that fresh render. Quantity inputs are never touched, so their component bindings
     survive. */
  if (!window.Shopify || !Shopify.theme || Shopify.theme.role === 'main') return;
  if (window.__spectrumPreviewRepaint) return;
  window.__spectrumPreviewRepaint = true;

  var timer = null;
  function swapHTML(selector, doc) {
    var from = doc.querySelector(selector);
    var to = document.querySelector(selector);
    if (from && to) to.innerHTML = from.innerHTML;
  }
  function repaint() {
    var url = '/cart?preview_theme_id=' + Shopify.theme.id + '&_fd=0&tt_heimdall_debug_dest=shopify';
    fetch(url)
      .then(function (r) { return r.text(); })
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        if (!doc.querySelector('.spectrum-pb, .spectrum-foot__value')) return; // guard: not our render
        ['.spectrum-pb', '.spectrum-cart-progress', '.spectrum-ryo-head__count'].forEach(function (s) {
          swapHTML(s, doc);
        });
        var dv = doc.querySelector('.spectrum-foot__value');
        var tv = document.querySelector('.spectrum-foot__value');
        if (dv && tv) tv.textContent = dv.textContent;
        doc.querySelectorAll('[data-key]').forEach(function (row) {
          var key = row.getAttribute('data-key');
          var srcPrice = row.querySelector('.spectrum-std__price');
          if (!key || !srcPrice) return;
          var esc = (window.CSS && CSS.escape) ? CSS.escape(key) : key.replace(/["\\]/g, '\\$&');
          var cur = document.querySelector('[data-key="' + esc + '"]');
          var curPrice = cur && cur.querySelector('.spectrum-std__price');
          if (curPrice) curPrice.innerHTML = srcPrice.innerHTML;
        });
      })
      .catch(function () {});
  }
  /* The cart's live update posts to /cart/change (or /cart/add) but does not emit a reliable DOM
     event in this theme, so hook fetch and repaint after any cart mutation. The repaint's own GET
     to /cart (no /change) is excluded, so there is no loop. */
  var origFetch = window.fetch;
  window.fetch = function (input) {
    var url = typeof input === 'string' ? input : (input && input.url) || '';
    var result = origFetch.apply(this, arguments);
    if (/\/cart\/(change|add|update|clear)(\.js)?/i.test(String(url))) {
      result.then(function () { clearTimeout(timer); timer = setTimeout(repaint, 250); }).catch(function () {});
    }
    return result;
  };
})();

/* ---- '-' stays pressable at the line minimum ---- */
(function () {
  if (window.__spectrumMinusEnabled) return;
  window.__spectrumMinusEnabled = true;
  /* Horizon disables '-' once quantity reaches the line minimum, because in the stock
     cart removal is the trash control. This cart has no trash — '-' at the minimum IS
     the remove control — so the button has to stay pressable there or the line can
     never be removed. Patching the component rather than the markup is what keeps it
     enabled across cart re-renders, each of which re-runs updateButtonStates. */
  function patch(C) {
    if (!C || !C.prototype || C.prototype.__spectrumMinusPatched) return;
    var orig = C.prototype.updateButtonStates;
    if (typeof orig !== 'function') return;
    C.prototype.__spectrumMinusPatched = true;
    C.prototype.updateButtonStates = function () {
      orig.apply(this, arguments);
      var refs = this.refs;
      if (refs && refs.minusButton && this.closest('.spectrum-std')) refs.minusButton.disabled = false;
    };
  }
  var name = 'cart-quantity-selector-component';
  if (customElements.get(name)) patch(customElements.get(name));
  else customElements.whenDefined(name).then(function () { patch(customElements.get(name)); });
  /* Rows whose component upgraded before this patch landed. */
  document.querySelectorAll('.spectrum-std .quantity-minus[disabled]').forEach(function (b) { b.disabled = false; });
})();
